import { HttpResponseBase } from "@angular/common/http";
import { ClassProvider, ErrorHandler, inject, Injectable, NgZone } from "@angular/core";
import { Router } from "@angular/router";
import { SentryErrorHandler } from "@sentry/angular";
import { ApiException } from "src/app/api/client";
import { sentryErrorHandlerOptions } from "../config/sentry.config";
import { ToastType } from "../enums/toast-type.enum";
import { ToastService } from "../services/toast-service.service";
import { AppHttpErrorResponse } from "./../models/app-http-error-response.model";
import { appErrorFalsePositiveList } from "./app-error-false-positive-list";

export const provideAppErrorHandler = (): ClassProvider => ({ provide: ErrorHandler, useClass: AppErrorHandler });

@Injectable()
export class AppErrorHandler extends SentryErrorHandler implements ErrorHandler {
  private readonly ngZone = inject(NgZone);
  private readonly router = inject(Router);
  private readonly toast = inject(ToastService);

  constructor() {
    super(sentryErrorHandlerOptions);
  }

  override handleError(error: unknown): void {
    super.handleError(error);

    if (navigator.onLine && error instanceof Error && (error?.name?.includes("ChunkLoadError") || error?.message?.includes("ChunkLoadError"))) {
      return;
    }

    try {
      if (
        error &&
        !(error instanceof HttpResponseBase) &&
        !ApiException.isApiException(error) &&
        !AppHttpErrorResponse.isInterceptedByApp(error) &&
        (!(error instanceof GeolocationPositionError) || error.code !== GeolocationPositionError.PERMISSION_DENIED)
      ) {
        this.handleApplicationError(error);
      }
    } catch (innerError) {
      super.handleError(innerError);
    }
  }

  /**
   * Handles an Error thrown within the Client
   *
   * @param error The Error object
   */
  protected handleApplicationError(error: unknown): void {
    if (typeof error === "string" || typeof error === "number" || typeof error === "boolean") {
      error = new Error(error.toString());
    }

    if (error && typeof error === "object") {
      const unknownRecord = <{ name?: unknown; message?: unknown }>error;
      if (
        appErrorFalsePositiveList.some(
          (x) =>
            (unknownRecord.name && typeof unknownRecord.name === "string" && unknownRecord.name.includes(x)) ||
            (unknownRecord.message && typeof unknownRecord.message === "string" && unknownRecord.message.includes(x))
        )
      ) {
        return;
      }
    }

    if (error instanceof Error) {
      // Google Maps, etc...
      if ([error.message?.toString().toLowerCase(), error.name?.toString().toLowerCase()].some((x) => x?.includes("google"))) {
        this.toast.showToast(error.message, ToastType.Error);
        console.error(error.message);
        return;
      }

      // Some Media errors, including video scanner
      if (
        ["NotAllowedError", "NotReadableError"].some(
          (str) => (error.message?.toString().includes(str) || error.name?.toString().includes(str)) ?? false
        )
      ) {
        this.toast.showToast(error.message, ToastType.Warning);
        console.error(error.message);
        return;
      }

      this.navigateToErrorPage({ message: error.message });
    }
  }

  private async navigateToErrorPage(queryParams?: Record<string, string | ReadonlyArray<string> | undefined>): Promise<boolean> {
    // eslint-disable-next-line no-useless-assignment
    let result: boolean = false;
    try {
      result = await this.ngZone.run(() => this.router.navigate(["/error"], { queryParams, skipLocationChange: true }));
    } catch (err) {
      if (err && err instanceof Error) {
        console.error(err.message);
      }
      return false;
    }
    if (!result) {
      const message = queryParams?.["message"]?.toString().trim();
      if (message) {
        this.toast.showToast(message, ToastType.Error);
      }
    }
    return result;
  }
}
